import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { io, Socket } from 'socket.io-client';
import { FaseOS } from '../types';
import { useFamilyStore } from '../stores/familyStore';

// Fix para ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface FaseMapProps {
  fase: FaseOS;
}

// Coordenadas conhecidas removidas como destino automático —
// preferimos destino_lat/lng persistidos; se não houver, geocode validado na API.
// Não inventar centro de SP / HOSPITAL genérico.

// Função para geocodificar via API (apenas resultados validados pelo backend)
const geocodeAddress = async (endereco: string): Promise<{ lat: number; lng: number } | null> => {
  console.log('🔍 Geocodificando endereço:', endereco);
  
  try {
    console.log('🌐 Fazendo requisição para API...');
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';
    const response = await fetch(
      `${apiUrl}/geocoding/geocode?q=${encodeURIComponent(endereco)}`
    );
    
    console.log('📡 Resposta da API:', response.status, response.ok);
    
    if (!response.ok) {
      console.warn('⚠️ Geocode sem resultado validado:', response.status);
      return null;
    }
    
    const data = await response.json();
    console.log('📊 Dados recebidos:', data);
    
    if (data.success && data.result && data.result.lat && data.result.lng) {
      const coords = {
        lat: parseFloat(data.result.lat),
        lng: parseFloat(data.result.lng)
      };
      console.log('✅ Coordenadas extraídas da API:', coords);
      console.log('📍 Provider usado:', data.result.provider);
      return coords;
    }
    
    console.log('⚠️ Nenhuma coordenada validada na API', data.code || data.error || '');
    return null;
    
  } catch (error) {
    console.warn('❌ Erro na geocodificação via API:', error);
    return null;
  }
};

/** Preferir coordenadas já salvas na fase; senão tentar geocode validado. */
const resolveDestinoCoords = async (
  fase: FaseOS
): Promise<{ lat: number; lng: number } | null> => {
  if (fase.destino_lat != null && fase.destino_lng != null) {
    const lat = parseFloat(String(fase.destino_lat));
    const lng = parseFloat(String(fase.destino_lng));
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      console.log('✅ Usando destino_lat/lng persistidos na fase');
      return { lat, lng };
    }
  }
  if (fase.endereco_destino && fase.endereco_destino !== 'Não informado') {
    return geocodeAddress(fase.endereco_destino);
  }
  return null;
};

const FaseMap: React.FC<FaseMapProps> = ({ fase }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const routeRef = useRef<L.Polyline | null>(null);
  const destinationMarkerRef = useRef<L.Marker | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCalculatingRouteRef = useRef<boolean>(false);
  const [destinoCoords, setDestinoCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<{ lat: number; lng: number } | null>(
    fase.ultima_lat && fase.ultima_lng 
      ? { lat: parseFloat(fase.ultima_lat.toString()), lng: parseFloat(fase.ultima_lng.toString()) }
      : null
  );
  const isMountedRef = useRef(true);
  const { ordemServico } = useFamilyStore();

  // Ícones personalizados profissionais e elegantes (MESMOS DO MOTORISTA)
  const createCustomIcon = (iconType: 'vehicle' | 'destination' | 'location') => {
    let iconConfig = {
      size: [42, 42],
      symbol: '🚗',
      bgColor: '#3B82F6',
      borderColor: '#1E40AF',
      textColor: '#FFFFFF'
    };

    switch (iconType) {
      case 'vehicle':
        iconConfig = {
          size: [48, 48],
          symbol: '🚗',
          bgColor: '#1E293B', // Azul escuro profissional
          borderColor: '#0F172A',
          textColor: '#FFFFFF'
        };
        break;
      case 'destination':
        iconConfig = {
          size: [44, 44],
          symbol: '🎯', // Alvo de destino
          bgColor: '#7C3AED', // Roxo elegante
          borderColor: '#5B21B6',
          textColor: '#FFFFFF'
        };
        break;
      case 'location':
        iconConfig = {
          size: [38, 38],
          symbol: '📍',
          bgColor: '#059669', // Verde elegante
          borderColor: '#047857',
          textColor: '#FFFFFF'
        };
        break;
    }
    
    return L.divIcon({
      className: 'custom-marker-elegant',
      html: `
        <div style="
          background: linear-gradient(145deg, ${iconConfig.bgColor} 0%, ${iconConfig.borderColor} 100%);
          width: ${iconConfig.size[0]}px;
          height: ${iconConfig.size[1]}px;
          border-radius: 12px;
          border: 2px solid rgba(255,255,255,0.9);
          box-shadow: 
            0 6px 20px rgba(0,0,0,0.15),
            0 0 0 1px rgba(255,255,255,0.2),
            inset 0 1px 0 rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          position: relative;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
        ">
          ${iconConfig.symbol}
          <div style="
            position: absolute;
            bottom: -6px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 6px solid ${iconConfig.bgColor};
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
          "></div>
        </div>
      `,
      iconSize: iconConfig.size as [number, number],
      iconAnchor: [iconConfig.size[0] / 2, iconConfig.size[1] + 6],
    });
  };

  // Função para atualizar posição do marcador no mapa
  const updateMarkerPosition = React.useCallback((lat: number, lng: number) => {
    if (!mapInstanceRef.current || !markerRef.current) return;
    
    markerRef.current.setLatLng([lat, lng]);
    
    // Atualizar popup com nova posição
    const motoristaNome = fase.motorista?.nome || 'Motorista';
    const veiculoInfo = fase.veiculo 
      ? (() => {
          const marcaModelo = (fase.veiculo?.marca && fase.veiculo?.modelo) 
            ? `${fase.veiculo.marca} ${fase.veiculo.modelo}`
            : fase.veiculo?.marca 
              ? fase.veiculo.marca
              : fase.veiculo?.modelo 
                ? fase.veiculo.modelo
                : '';
          return marcaModelo ? `${marcaModelo} (${fase.veiculo.placa})` : fase.veiculo.placa;
        })()
      : '';
    
    const popupContent = `
      <div class="p-4 min-w-56">
        <div class="flex items-center space-x-3 mb-3">
          <div class="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
            🚗
          </div>
          <div>
            <h4 class="font-bold text-gray-900 text-sm">${motoristaNome}${veiculoInfo ? ` - ${veiculoInfo}` : ''}</h4>
            <p class="text-xs text-gray-600">${fase.nome_fase}</p>
          </div>
        </div>
        
        <div class="space-y-2 text-xs">
          <div class="flex justify-between items-center p-2 bg-gray-50 rounded">
            <span class="font-medium text-gray-700">Destino:</span>
            <span class="text-gray-900 font-semibold">${fase.endereco_destino || 'Não informado'}</span>
          </div>
          
          <div class="flex justify-between items-center p-2 bg-blue-50 rounded">
            <span class="font-medium text-blue-700">Status:</span>
            <span class="text-blue-900 font-semibold">${fase.status_fase}</span>
          </div>
          
          <div class="flex justify-between items-center p-2 bg-green-50 rounded">
            <span class="font-medium text-green-700">Posição:</span>
            <span class="text-green-900 font-mono text-xs">${lat.toFixed(6)}, ${lng.toFixed(6)}</span>
          </div>
        </div>
      </div>
    `;
    markerRef.current.setPopupContent(popupContent);
    
    // Recalcular rota se houver destino
    if (destinoCoords) {
      calculateRoute(lat, lng, destinoCoords.lat, destinoCoords.lng);
    }
  }, [fase, destinoCoords]);

  // Função para buscar posição atual do backend (polling)
  const fetchCurrentPosition = async () => {
    if (!ordemServico || fase.status_fase !== 'Em Rota') return;
    
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';
      const response = await fetch(`${apiUrl}/os/public/${ordemServico.codigo_acesso}`);
      
      if (response.ok) {
        const data = await response.json();
        const updatedFase = data.os?.fases?.find((f: any) => f.id === fase.id);
        
        if (updatedFase && updatedFase.ultima_lat && updatedFase.ultima_lng) {
          const newLat = parseFloat(updatedFase.ultima_lat.toString());
          const newLng = parseFloat(updatedFase.ultima_lng.toString());
          
          // Atualizar posição se mudou
          if (currentPosition) {
            const latDiff = Math.abs(currentPosition.lat - newLat);
            const lngDiff = Math.abs(currentPosition.lng - newLng);
            
            // Atualizar se mudou mais de 0.0001 graus (aproximadamente 11 metros)
            if (latDiff > 0.0001 || lngDiff > 0.0001) {
              setCurrentPosition({ lat: newLat, lng: newLng });
              updateMarkerPosition(newLat, newLng);
              console.log('📍 Posição atualizada via polling:', { lat: newLat, lng: newLng });
            }
          } else {
            setCurrentPosition({ lat: newLat, lng: newLng });
            updateMarkerPosition(newLat, newLng);
          }
        }
      }
    } catch (error) {
      console.error('❌ Erro ao buscar posição atual:', error);
    }
  };

  // Função para conectar ao WebSocket
  const connectWebSocket = () => {
    if (!ordemServico?.codigo_acesso || fase.status_fase !== 'Em Rota') return;
    
    // Desconectar socket anterior se existir
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    
    console.log('🔌 Conectando ao WebSocket da família...');
    const backendUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3002';
    
    socketRef.current = io(backendUrl, {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });
    
    socketRef.current.on('connect', () => {
      console.log('✅ WebSocket conectado - família');
      // Entrar na sala da OS usando código de acesso
      socketRef.current?.emit('join_os_room', { codigo_acesso: ordemServico.codigo_acesso });
    });
    
    // Escutar atualizações de posição
    socketRef.current.on('posicao_atualizada', (data: any) => {
      console.log('📍 Posição atualizada via WebSocket:', data);
      
      // Verificar se é para esta fase
      if (data.faseId === fase.id && data.lat && data.lng) {
        const newLat = parseFloat(data.lat);
        const newLng = parseFloat(data.lng);
        
        setCurrentPosition({ lat: newLat, lng: newLng });
        updateMarkerPosition(newLat, newLng);
      }
    });
    
    socketRef.current.on('disconnect', () => {
      console.log('🔌 WebSocket desconectado - família');
    });
    
    socketRef.current.on('connect_error', (error) => {
      console.error('❌ Erro ao conectar WebSocket:', error);
    });
  };

  // Função para calcular rota usando OSRM (MESMA DO MOTORISTA)
  const calculateRoute = async (startLat: number, startLng: number, endLat: number, endLng: number) => {
    // Verificar se o mapa ainda existe
    const map = mapInstanceRef.current;
    if (!map || !isMountedRef.current) {
      console.warn('⚠️ Mapa não disponível para calcular rota');
      return;
    }

    // Prevenir múltiplas chamadas simultâneas
    if (isCalculatingRouteRef.current) {
      console.log('⏳ Rota já está sendo calculada, ignorando chamada duplicada...');
      return;
    }

    // Limpar rota existente ANTES de iniciar cálculo
    if (routeRef.current && mapInstanceRef.current) {
      try {
        mapInstanceRef.current.removeLayer(routeRef.current);
        console.log('🗑️ Rota anterior removida');
      } catch (error) {
        console.warn('⚠️ Erro ao remover rota anterior:', error);
      }
      routeRef.current = null;
    }

    isCalculatingRouteRef.current = true;
    setIsCalculatingRoute(true);
    console.log('🗺️ Calculando rota usando OSRM (OpenStreetMap nativo)...');
    
    try {
      // Usar OSRM diretamente (OpenStreetMap) - MESMO DO MOTORISTA
      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${startLng},${startLat};${endLng},${endLat}?overview=full&geometries=geojson&steps=true`
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('🗺️ Resposta do OSRM:', data);
        
        // Verificar novamente se o mapa ainda existe antes de adicionar a rota
        if (!mapInstanceRef.current || !isMountedRef.current) {
          console.warn('⚠️ Mapa foi desmontado durante cálculo da rota');
          return;
        }
        
        if (data.routes && data.routes[0]) {
          const routeData = data.routes[0];
          
          // OSRM já retorna no formato correto para Leaflet
          const latLngPoints = routeData.geometry.coordinates.map((coord: number[]) => {
            return [coord[1], coord[0]]; // [lat, lng]
          });
          
          console.log('🗺️ Pontos da rota OSRM:', latLngPoints);
          console.log('🗺️ Origem:', [startLat, startLng]);
          console.log('🗺️ Destino:', [endLat, endLng]);
          
          // Criar linha da rota (azul profissional com transparência) - MESMO DO MOTORISTA
          const routeLine = L.polyline(latLngPoints, {
            color: '#3B82F6',        // Azul elegante
            weight: 5,               // Espessura mais sutil
            opacity: 0.8,            // Transparência profissional
            dashArray: '12, 8',      // Traços mais suaves
            lineCap: 'round',        // Pontas arredondadas
            lineJoin: 'round'        // Junções arredondadas
          });
          
          // Remover rota anterior uma vez mais antes de adicionar nova (proteção extra)
          if (routeRef.current && mapInstanceRef.current) {
            try {
              mapInstanceRef.current.removeLayer(routeRef.current);
              console.log('🗑️ Rota anterior removida (proteção extra)');
            } catch (error) {
              console.warn('⚠️ Erro ao remover rota anterior (proteção extra):', error);
            }
          }

          // Adicionar ao mapa (verificar novamente)
          if (mapInstanceRef.current && isMountedRef.current) {
            routeLine.addTo(mapInstanceRef.current);
            routeRef.current = routeLine;
            console.log('✅ Rota OSRM calculada e desenhada');
          }
        }
      }
    } catch (error) {
      console.error('❌ Erro ao calcular rota OSRM:', error);
    } finally {
      isCalculatingRouteRef.current = false;
      if (isMountedRef.current) {
        setIsCalculatingRoute(false);
      }
    }
  };

  useEffect(() => {
    if (!mapRef.current || !fase.ultima_lat || !fase.ultima_lng) {
      return;
    }

    const lat = parseFloat(fase.ultima_lat.toString());
    const lng = parseFloat(fase.ultima_lng.toString());

    // Criar mapa com zoom otimizado
    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      dragging: true,
      touchZoom: true
    }).setView([lat, lng], 16);
    
    mapInstanceRef.current = map;

    // Adicionar camada OpenStreetMap com estilo moderno
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
      minZoom: 3
    }).addTo(map);

    // Adicionar marcador do motorista (MESMO ÍCONE DO MOTORISTA)
    markerRef.current = L.marker([lat, lng], {
      icon: createCustomIcon('vehicle')
    }).addTo(map);

    // Criar popup moderno com informações da fase
    const popupContent = `
      <div class="p-4 min-w-56">
        <div class="flex items-center space-x-3 mb-3">
          <div class="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
            🚗
          </div>
          <div>
            <h4 class="font-bold text-gray-900 text-sm">${fase.motorista?.nome || 'Motorista'}${fase.veiculo ? ` - ${(() => {
              const marcaModelo = (fase.veiculo.marca && fase.veiculo.modelo) 
                ? `${fase.veiculo.marca} ${fase.veiculo.modelo}`
                : fase.veiculo.marca 
                  ? fase.veiculo.marca
                  : fase.veiculo.modelo 
                    ? fase.veiculo.modelo
                    : '';
              return marcaModelo ? `${marcaModelo} (${fase.veiculo.placa})` : fase.veiculo.placa;
            })()}` : ''}</h4>
            <p class="text-xs text-gray-600">${fase.nome_fase}</p>
          </div>
        </div>
        
        <div class="space-y-2 text-xs">
          <div class="flex justify-between items-center p-2 bg-gray-50 rounded">
            <span class="font-medium text-gray-700">Destino:</span>
            <span class="text-gray-900 font-semibold">${fase.endereco_destino || 'Não informado'}</span>
          </div>
          
          <div class="flex justify-between items-center p-2 bg-blue-50 rounded">
            <span class="font-medium text-blue-700">Status:</span>
            <span class="text-blue-900 font-semibold">${fase.status_fase}</span>
          </div>
          
          <div class="flex justify-between items-center p-2 bg-green-50 rounded">
            <span class="font-medium text-green-700">Posição:</span>
            <span class="text-green-900 font-mono text-xs">${lat.toFixed(6)}, ${lng.toFixed(6)}</span>
          </div>
        </div>
      </div>
    `;

    markerRef.current.bindPopup(popupContent);

    // Destino: preferir coords persistidas; senão geocode validado (sem fallback falso)
    if (fase.endereco_destino && fase.endereco_destino !== 'Não informado') {
      resolveDestinoCoords(fase).then((coords) => {
        // Verificar se o componente ainda está montado e o mapa existe
        if (!isMountedRef.current || !mapInstanceRef.current) {
          console.warn('⚠️ Componente desmontado ou mapa não disponível');
          return;
        }

        if (!coords) {
          console.warn('⚠️ Destino sem coordenadas validadas — mapa sem pino/rota de destino');
          try {
            const currentMap = mapInstanceRef.current;
            if (currentMap && currentMap.getContainer()) {
              currentMap.setView([lat, lng], 16);
            }
          } catch (viewError) {
            console.warn('⚠️ Não foi possível ajustar a view do mapa:', viewError);
          }
          return;
        }

        const currentMap = mapInstanceRef.current;
        
        setDestinoCoords(coords);
        
        // Adicionar marcador de destino (MESMO ÍCONE DO MOTORISTA)
        // Verificar novamente antes de adicionar
        if (currentMap && currentMap.getContainer()) {
          // Remover marcador anterior se existir
          if (destinationMarkerRef.current) {
            currentMap.removeLayer(destinationMarkerRef.current);
          }

          destinationMarkerRef.current = L.marker([coords.lat, coords.lng], {
            icon: createCustomIcon('destination')
          }).addTo(currentMap);

          // Criar popup para destino
          const destPopupContent = `
            <div class="p-3 min-w-48">
              <div class="flex items-center space-x-2 mb-2">
                <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  🎯
                </div>
                <div>
                  <h4 class="font-semibold text-gray-900 text-sm">Destino</h4>
                  <p class="text-xs text-gray-600">${fase.nome_fase}</p>
                </div>
              </div>
              
              <div class="p-2 bg-purple-50 rounded text-xs">
                <span class="font-medium text-purple-700">Endereço:</span>
                <p class="text-purple-900 mt-1">${fase.endereco_destino}</p>
              </div>
            </div>
          `;

          destinationMarkerRef.current.bindPopup(destPopupContent);

          // Calcular e exibir rota REAL usando OSRM (MESMO DO MOTORISTA)
          calculateRoute(lat, lng, coords.lat, coords.lng);

          // Ajustar view para mostrar origem e destino
          const bounds = L.latLngBounds([[lat, lng], [coords.lat, coords.lng]]);
          currentMap.fitBounds(bounds, { padding: [30, 30] });
        }
      }).catch((error) => {
        console.error('❌ Erro ao resolver destino:', error);
      });
    } else {
      // Centralizar apenas na posição do motorista
      map.setView([lat, lng], 16);
    }

    // Adicionar controles de zoom personalizados
    const zoomControl = L.control.zoom({
      position: 'bottomright',
      zoomInTitle: 'Zoom In',
      zoomOutTitle: 'Zoom Out'
    }).addTo(map);

    return () => {
      // Marcar componente como desmontado
      isMountedRef.current = false;
      
      // Limpar marcadores e rotas
      if (destinationMarkerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(destinationMarkerRef.current);
        destinationMarkerRef.current = null;
      }
      
      if (markerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(markerRef.current);
        markerRef.current = null;
      }
      
      if (routeRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(routeRef.current);
        routeRef.current = null;
      }
      
      // Remover mapa
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [fase]);

  // Resetar flag de montagem quando fase muda
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, [fase]);

  // Conectar WebSocket e iniciar polling quando fase estiver em rota
  useEffect(() => {
    if (fase.status_fase !== 'Em Rota' || !ordemServico?.codigo_acesso) {
      // Limpar conexões se fase não estiver em rota
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Conectar ao WebSocket
    connectWebSocket();

    // Iniciar polling periódico a cada 15 segundos
    pollingIntervalRef.current = setInterval(() => {
      fetchCurrentPosition();
    }, 15000);

    // Buscar posição inicial imediatamente
    fetchCurrentPosition();

    // Cleanup
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [fase.status_fase, fase.id, ordemServico?.codigo_acesso]);

  // Atualizar marcador quando currentPosition mudar
  useEffect(() => {
    if (currentPosition && markerRef.current && mapInstanceRef.current) {
      updateMarkerPosition(currentPosition.lat, currentPosition.lng);
    }
  }, [currentPosition, updateMarkerPosition]);

  if (!fase.ultima_lat || !fase.ultima_lng) {
    return (
      <div className="flex items-center justify-center h-full bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-3">📍</div>
          <p className="text-sm font-medium">Aguardando posição GPS</p>
          <p className="text-xs text-gray-400 mt-1">O motorista será localizado em breve</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={mapRef} 
      className="w-full h-full rounded-lg" 
      style={{ 
        margin: 0, 
        padding: 0, 
        overflow: 'hidden',
        borderRadius: '8px'
      }} 
    />
  );
};

export default FaseMap;
